import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import sampleProfileImage from "assets/images/sample.png";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { getAPICall } from "lib/callApi";
import { GET_USER_KPIS } from "lib/constants";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { generateColorFromString } from "utils/GenerateColorFromString/GenerateColorFromString";
import { numberFormatter } from "utils/HelperFunctions";
import "../../../styles/performance.scss";
import TooltipLight from "utils/TooltipLight";
import FlameIcon from "components/Icons/FlameIcon";
import KpiBreakdownPopover from "./KpiBreakdownPopover";

const TableHeader = styled(TableHead)({
  [`& .MuiTableCell-root p`]: {
    fontSize: "0.8rem",
    fontWeight: "400",
    lineHeight: "1rem",
    color: "#666",
  },
  [`& .MuiTableCell-root`]: {
    padding: "0.75rem 0.5rem",
  },
});

const TableRowSlim = styled(TableRow)({
  [`& .MuiTableCell-root`]: {
    fontSize: "1rem",
    lineHeight: "1.2rem",
    padding: "0.75rem 0.5rem",
  },
});

const SortableLabel = React.memo(
  ({ labelKey, children, order, orderBy, handleRequestSort }) => (
    <TableSortLabel
      active={orderBy === labelKey}
      direction={orderBy === labelKey ? order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
    >
      {children}
      {orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.order === nextProps.order &&
    prevProps.orderBy === nextProps.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

function ListingTable({
  pagination,
  changeURL,
  handleTableFilterModalOpen,
  columnsWithNames,
  dateRange,
}) {
  const { order, orderBy, pageSize, pageNumber } = pagination;
  const data = useSelector((state) => state.leaderboard.leaderboardList || []);
  const totalRows = useSelector((state) => state.leaderboard.totalRows || 0);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverContent, setPopoverContent] = useState("");
  const [userPopoverMap, setUserPopoverMap] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const rowRefs = useRef({});
  const MINIMUM_STREAK_DAYS = 3;

  useEffect(() => {
    setUserPopoverMap({});
  }, [dateRange]);

  const getPopoverPosition = (index) => {
    const rowRef = rowRefs.current[index];
    if (rowRef) {
      const rect = rowRef.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (rect.bottom > windowHeight - 150) {
        return {
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        };
      }
    }

    return {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "center",
      },
    };
  };

  const popoverPosition = getPopoverPosition(selectedRow);

  const setRowRef = (index, element) => {
    rowRefs.current[index] = element;
  };

  const renderPopover = (graphData, userName) => (
    <KpiBreakdownPopover graphData={graphData} userName={userName} />
  );

  const fetchPopoverContent = async (user) => {
    if (user.id in userPopoverMap) {
      setPopoverContent(renderPopover(userPopoverMap[user.id], user.name));
    } else {
      const result = await getAPICall(GET_USER_KPIS, true, {
        userId: user.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      if (result?.data?.success) {
        const dataStructure = { labels: [], data: [], colors: [] };
        result.data.data.forEach((entry) => {
          if (Number(entry.totalCount)) {
            dataStructure.labels.push(entry.kpiActionName);
            dataStructure.data.push(Number(entry.totalCount));
            dataStructure.colors.push(generateColorFromString(entry.kpiActionName));
          }
        });
        setUserPopoverMap((prevMap) => ({
          ...prevMap,
          [user.id]: dataStructure,
        }));
        setPopoverContent(renderPopover(dataStructure, user.name));
      }
    }
  };

  const handlePopoverOpen = async (event, user, index) => {
    setPopoverAnchor(event.currentTarget);
    await fetchPopoverContent(user);
    setPopoverOpen(true);
    setSelectedRow(index);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverContent("");
    setPopoverOpen(false);
    setSelectedRow(null);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    changeURL(null, { ...pagination, order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handlePageChange = (_, newPage) => {
    changeURL(null, { ...pagination, pageNumber: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    changeURL(null, {
      ...pagination,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  const renderRowValue = (row, id) => {
    const value = parseInt(row?.[id] || 0, 10);
    if (id.indexOf("Streak") >= 0) {
      const variant = value >= MINIMUM_STREAK_DAYS ? "basic" : "basicgray";
      return <FlameIcon variant={variant} count={value} />;
    }
    return (
      <>
        {numberFormatter(value)}
        {id === "quotaAttainment" ? "%" : ""}
      </>
    );
  };

  return (
    <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
      <Table aria-label="simple table">
        <TableHeader key="leader-board-table" sx={{ display: "table-header-group" }}>
          <TableRow>
            <TableCell width="10%" align="center" key="ranking">
              <SortableLabel
                labelKey="rank"
                order={order}
                orderBy={orderBy}
                handleRequestSort={handleRequestSort}
              >
                <MDTypography>Ranking</MDTypography>
                <TooltipLight title="Calculated by Achieved Points">
                  <InfoOutlinedIcon
                    sx={{
                      cursor: "pointer",
                      ml: 1,
                      color: "#666",
                    }}
                  />
                </TooltipLight>
              </SortableLabel>
            </TableCell>
            <TableCell width="20%" key="name">
              <SortableLabel
                labelKey="name"
                order={order}
                orderBy={orderBy}
                handleRequestSort={handleRequestSort}
              >
                <MDTypography ml={9}>Name</MDTypography>
              </SortableLabel>
            </TableCell>
            {columnsWithNames?.other?.length
              ? columnsWithNames.other.map((option) => (
                  // option.id can be undefine and considering use index instead
                  <TableCell align="center" key={option.id}>
                    <SortableLabel
                      labelKey={option.id}
                      key={option.id}
                      order={order}
                      orderBy={orderBy}
                      handleRequestSort={handleRequestSort}
                    >
                      <MDTypography>{option.name || ""}</MDTypography>
                    </SortableLabel>
                  </TableCell>
                ))
              : null}
            {columnsWithNames?.kpis?.length
              ? columnsWithNames.kpis.map((option) => (
                  <TableCell align="center" key={option.id}>
                    <SortableLabel
                      labelKey={`kpi-${option.id}`}
                      key={`kpi-${option.id}`}
                      order={order}
                      orderBy={orderBy}
                      handleRequestSort={handleRequestSort}
                    >
                      <MDTypography>{option.name}</MDTypography>
                    </SortableLabel>
                  </TableCell>
                ))
              : null}
            <TableCell align="center" key="add-metric">
              <MDTypography
                sx={{
                  cursor: "pointer",
                  color: "cornflowerblue !important",
                  fontWeight: "600 !important",
                }}
                onClick={handleTableFilterModalOpen}
              >
                + Add Metric
              </MDTypography>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((row, index) => (
            <TableRowSlim
              key={row?.id || index}
              sx={{
                "& :hover": {
                  cursor: "pointer",
                },
                background: index % 2 ? "#fff" : "#f5f5f5",
              }}
              onClick={(event) => handlePopoverOpen(event, row, index)}
              ref={(e) => setRowRef(index, e)}
            >
              <TableCell align="center" key={`ranking-${row?.rank}`}>
                {numberFormatter(parseInt(row?.rank || 0, 10))}
              </TableCell>
              <TableCell key={`name-${row.name}`}>
                <Stack direction="row" spacing={4} alignItems="center">
                  <MDAvatar variant="circular" src={row.profilePicture || sampleProfileImage} />
                  <MDBox mr={2} sx={{ color: "inherit" }}>
                    {row.name}
                  </MDBox>
                </Stack>
              </TableCell>
              {columnsWithNames?.other?.length
                ? columnsWithNames.other.map((option) =>
                    option?.id ? (
                      <TableCell align="center" key={`${row.id}-${option.id}`}>
                        {renderRowValue(row, option.id)}
                      </TableCell>
                    ) : null
                  )
                : null}
              {columnsWithNames?.kpis?.length
                ? columnsWithNames?.kpis?.map((option) =>
                    option?.id ? (
                      <TableCell align="center" key={`${row.id}-${option.id}`}>
                        {numberFormatter(parseInt(row?.[`kpi-${option.id}`] || 0, 10))}
                      </TableCell>
                    ) : null
                  )
                : null}
              <TableCell align="center" key="metric" />
            </TableRowSlim>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 25, 50]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
        component="div"
        count={totalRows}
        rowsPerPage={pageSize}
        page={pageNumber}
        sx={{ flex: "1 1 auto" }}
      />
      {popoverOpen && (
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={popoverPosition.anchorOrigin}
          transformOrigin={popoverPosition.transformOrigin}
          sx={{
            "& .MuiPopover-paper": {
              p: 0,
            },
          }}
        >
          {popoverContent}
        </Popover>
      )}
    </TableContainer>
  );
}

export default ListingTable;
